import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  tenantApplicationsService,
  publicApplicationService,
} from '../services/tenantApplications';
import { tenantsApi } from '../services/tenants';
import { QUERY_KEYS } from './queryKeys';
import type {
  TenantApplication,
  TenantApplicationStatus,
  LandlordPendingApplication,
  CreateTenantInvitePayload,
  UpdateApplicationStepPayload,
} from '../types/tenantApplication';
import apiClient from '../services/api';

// ─── Manager hooks ────────────────────────────────────────────────────────────

export function useTenantApplications(status?: TenantApplicationStatus) {
  return useQuery({
    queryKey: QUERY_KEYS.tenantApplications.list(status),
    queryFn: () => tenantApplicationsService.listApplications(status),
    select: (data) => (Array.isArray(data) ? data : []),
  });
}

export function useTenantApplication(id: number) {
  return useQuery({
    queryKey: QUERY_KEYS.tenantApplications.detail(id),
    queryFn: () => tenantApplicationsService.getApplication(id),
    enabled: !!id,
  });
}

/** Nullable-id variant, safe to call with null; query is disabled when id is null */
export function useGetTenantApplication(id: number | null) {
  return useQuery({
    queryKey: QUERY_KEYS.tenantApplications.detail(id ?? 0),
    queryFn: () => apiClient.get(`/tenant-applications/${id}`).then((r) => r.data),
    enabled: id !== null,
  });
}

export function useCreateTenantInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateTenantInvitePayload) =>
      tenantApplicationsService.createInvite(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.tenantApplications.lists() });
    },
  });
}

export function useApproveTenantApplication() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (id: number) => tenantApplicationsService.approve(id),
    onSuccess: (data: { leaseId?: number | null }) => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.tenantApplications.all });
      if (data?.leaseId) {
        navigate(`/leases?leaseId=${data.leaseId}`);
      }
    },
  });
}

export function useRejectTenantApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      tenantApplicationsService.reject(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.tenantApplications.all });
    },
  });
}

export function useSubmitToLandlord() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => tenantApplicationsService.submitToLandlord(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.tenantApplications.all });
    },
  });
}

export function useCancelTenantApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => tenantApplicationsService.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.tenantApplications.all });
    },
  });
}

export function useResendTenantInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => tenantApplicationsService.resend(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.tenantApplications.all });
    },
  });
}

// ─── Public hooks (no JWT) ────────────────────────────────────────────────────

export function useVerifyApplicationToken(token: string) {
  return useQuery({
    queryKey: QUERY_KEYS.tenantApplications.public(token),
    queryFn: () => publicApplicationService.verifyToken(token),
    enabled: !!token,
    retry: false,
  });
}

export function useUpdateApplicationStep(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateApplicationStepPayload) =>
      publicApplicationService.updateStep(token, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.tenantApplications.public(token) });
    },
  });
}

export function useSubmitApplication(token: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => publicApplicationService.submit(token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.tenantApplications.public(token) });
    },
  });
}

// ─── CLIENT self-service ──────────────────────────────────────────────────────

export function useMyApplication() {
  return useQuery<TenantApplication>({
    queryKey: QUERY_KEYS.tenantApplications.mine(),
    queryFn: () => tenantApplicationsService.getMyApplication(),
    retry: false,
  });
}

// ─── Landlord KYC review hooks ────────────────────────────────────────────────

export function useLandlordPendingApplications() {
  return useQuery<LandlordPendingApplication[]>({
    queryKey: QUERY_KEYS.tenantApplications.landlordPending(),
    queryFn: () => tenantsApi.getLandlordPending(),
    select: (data) => (Array.isArray(data) ? data : []),
  });
}

export function useLandlordApplicationHistory() {
  return useQuery<LandlordPendingApplication[]>({
    queryKey: QUERY_KEYS.tenantApplications.landlordHistory(),
    queryFn: () => tenantsApi.getLandlordHistory(),
    select: (data) => (Array.isArray(data) ? data : []),
  });
}

export function useLandlordApprove() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (applicationId: number) => tenantsApi.landlordApprove(applicationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.tenantApplications.landlordPending() });
    },
  });
}

export function useLandlordReject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      tenantsApi.landlordReject(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QUERY_KEYS.tenantApplications.landlordPending() });
    },
  });
}
